import React, { useState, useEffect, useRef } from 'react';
import { Grid, CardContent, LinearProgress } from '@material-ui/core';
import { useHistory } from 'react-router';
import moment from 'moment';
import FilterDropdown from '../atom/FilterDropdown';
import TablePager from '../atom/TablePager';
import BookingTable from './BookingTable';

import { useNotify } from '../../hooks/useNotify';
import { useSwaggerClient } from '../../hooks/useSwaggerClient';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';

const GuestPageBookingTable = ({ label, guestId, isAgent, future, isAdmin, autoOpenCancelForId, onAutoOpenHandled, onAutoOpenError }) => {
    const history = useHistory();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [filters, setFilters] = useState({ status: 'all' });
    const { notifyError } = useNotify();
    const { showModal } = useModals();
    const autoOpenFired = useRef(false);

    const [bookingsData, bookingsLoading, bookingsLoadingError, setBookingsData] = useSwaggerClient(
        'users',
        'getBookingsByUserId',
        {
            userId: guestId,
            page,
            pageSize,
            filters: JSON.stringify(filters),
            future: future || false
        }
    );
    // wire guestLoadingError to notifyError
    useEffect(() => {
        // wire error to notifyerror
        if (bookingsLoadingError) {
            notifyError('Guest bookings cannot be loaded');
            history.push('/');
        }
    }, [bookingsLoadingError, notifyError, history]);

    const onCancelFinished = (bookingItemId, refund) => {
        setBookingsData({
            ...bookingsData,
            bookingItems: bookingsData.bookingItems.map(item =>
                item.id === bookingItemId
                    ? {
                          ...item,
                          status: refund ? 'cancelled_full_refund' : 'cancelled',
                          refund
                      }
                    : item
            )
        });
    };

    // Auto-open cancel modal when autoOpenCancelForId matches a booking in our data
    useEffect(() => {
        if (!autoOpenCancelForId || !bookingsData?.bookingItems || autoOpenFired.current) return;

        const matchingItem = bookingsData.bookingItems.find(item => item.id === autoOpenCancelForId);
        if (!matchingItem) return;

        const isGuest = !isAgent && !isAdmin;
        const isFuture = moment(matchingItem.activity_start).isAfter(moment());
        const isCancellable =
            (matchingItem.status === 'confirmed' || matchingItem.status === 'unconfirmed') && isFuture;

        autoOpenFired.current = true;

        if (isGuest && isCancellable) {
            showModal(MODAL_TYPE.CANCEL_BOOKINGITEM, {
                bookingItem: matchingItem,
                onCancelFinished
            });
            onAutoOpenHandled?.();
        } else if (isGuest && !isCancellable) {
            onAutoOpenError?.(matchingItem);
        }
    }, [autoOpenCancelForId, bookingsData, isAgent, isAdmin, showModal, onAutoOpenHandled, onAutoOpenError]);

    return (
        <Grid item xs={12} container alignContent="flex-start">
            {!bookingsData?.bookingItems && bookingsLoading && (
                <CardContent>
                    <div className="search-loading">
                        <div className="mb-2">Loading Bookings</div>
                        <LinearProgress className="search-bar" />
                    </div>
                </CardContent>
            )}
            <Grid item xs={12} sm={6}>
                <h2>
                    {label}
                    <span>{bookingsData?.bookingItemsCount} Activities</span>
                </h2>
            </Grid>
            {(filters.status !== 'all' || bookingsData?.bookingItemsCount > 0) && (
                <Grid item container xs={12} sm={6} alignContent="flex-end">
                    <FilterDropdown
                        justify="flex-end"
                        label=""
                        value={filters.status}
                        setValue={val => {
                            setFilters(filter => ({ ...filter, status: val }));
                        }}
                        options={[
                            { value: 'all', name: 'All' },
                            { value: 'unconfirmed', name: 'Unconfirmed' },
                            { value: 'confirmed', name: 'Confirmed' },
                            { value: 'denied', name: 'Denied' },
                            // no need to change cancelled here, we filter on backend site
                            { value: 'cancelled', name: 'Cancelled' }
                        ]}
                    />
                </Grid>
            )}
            <Grid item xs={12}>
                {bookingsData?.bookingItemsCount === 0 && <h5>No bookings found</h5>}
                <BookingTable
                    onGuestPage={true}
                    isAgent={isAgent}
                    isAdmin={isAdmin}
                    bookingItems={bookingsData?.bookingItems}
                    onCancelFinished={onCancelFinished}
                />
                <TablePager
                    total={bookingsData?.bookingItemsCount}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                />
            </Grid>
        </Grid>
    );
};

export default GuestPageBookingTable;
